import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type { AuditLogPage, AuditLogRecordIdInput } from './audit-log.types'

export const AUDIT_LOG_SUBJECTS = Object.freeze({
  SALES_ORDER: 'sales-orders',
  CUSTOMER: 'customers',
  PRODUCT: 'products',
  DESIGN: 'designs',
  USER: 'users',
  LOCATION: 'locations',
  CASH_DRAWER_SETTINGS: 'cash-drawer-settings',
  CUSTOMER_BILLING_DEFAULTS: 'customer-billing-defaults',
  TAX_SETTINGS: 'tax-settings',
  FEE_SETTINGS: 'fee-settings',
  SNAPDRAGON_SETTINGS: 'snapdragon-settings',
})

export type AuditLogSubject = (typeof AUDIT_LOG_SUBJECTS)[keyof typeof AUDIT_LOG_SUBJECTS]

/** The record whose history a query loads; singleton settings subjects ignore the record id. */
export interface AuditLogQueryKeyEntries {
  organizationId: OrganizationId
  subject?: AuditLogSubject | undefined
  recordId?: AuditLogRecordIdInput | undefined
  pageSize?: number | undefined
}

/** Query functions read the key and the page's cursor (null for the first page). */
type AuditLogQueryContext = Pick<
  QueryFunctionContext<[AuditLogQueryKeyEntries], string | null>,
  'queryKey' | 'pageParam'
>

const auditLogSubjects: ReadonlySet<string> = new Set(Object.values(AUDIT_LOG_SUBJECTS))
const singletonAuditLogPaths: Partial<Record<AuditLogSubject, string>> = {
  [AUDIT_LOG_SUBJECTS.CASH_DRAWER_SETTINGS]: '/settings/cash-drawer/audit-log',
  [AUDIT_LOG_SUBJECTS.CUSTOMER_BILLING_DEFAULTS]: '/customer-billing-defaults/audit-log',
  [AUDIT_LOG_SUBJECTS.TAX_SETTINGS]: '/tax-settings/audit-log',
  [AUDIT_LOG_SUBJECTS.FEE_SETTINGS]: '/fee-settings/audit-log',
  [AUDIT_LOG_SUBJECTS.SNAPDRAGON_SETTINGS]: '/snapdragon/settings/sales-order-auto-print/audit-log',
}

function isAuditLogSubject(subject: unknown): subject is AuditLogSubject {
  return typeof subject === 'string' && auditLogSubjects.has(subject)
}

// JavaScript callers can pass any subject, so it is checked before it becomes part of a URL.
function requireAuditLogSubject(subject: unknown): AuditLogSubject {
  if (!isAuditLogSubject(subject)) {
    throw new Error(`Unsupported audit-log subject: ${String(subject)}`)
  }

  return subject
}

export async function getAuditLog({
  queryKey: [{ organizationId, subject, recordId, pageSize }],
  pageParam,
}: AuditLogQueryContext) {
  const resource = requireAuditLogSubject(subject)
  const params = {
    page_size: pageSize,
    ...(pageParam ? { cursor: pageParam } : {}),
  }

  const path =
    singletonAuditLogPaths[resource] ??
    `/${resource}/${encodeURIComponent(String(recordId))}/audit-log`

  return axios.get<AuditLogPage>(path, buildAdminRequestConfig({ organizationId, params }))
}
